import AppError from '../errors/AppError';
import { ICNPJaData } from '../interfaces/ICNPJaData';
import { ICustomer } from '../interfaces/ICustomer';
import { IPaginationReturn } from '../interfaces/IPaginationReturn';
import IUser from '../interfaces/IUser';

import { useRequest } from './useRequest';

const useCustomerService = () => {
  const request = useRequest<ICustomer>();
  const requestCNPJa = useRequest<ICNPJaData>();
  const path = 'customers';

  const listAll = async (queryParams: string): Promise<IPaginationReturn<ICustomer[]>> => {
    return await request
      .getManyPaginated({ path: `${path}?${queryParams}`, sendAuthorization: true })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const listDynamically = async (queryParams: string): Promise<IPaginationReturn<ICustomer[]>> => {
    const userSession = localStorage.getItem('@PEDIDO-WEB:user');

    const user: IUser = userSession ? JSON.parse(userSession) : null;

    const idSalesRegion = user.seller?.salesRegion?.id ? user.seller?.salesRegion?.id : null;

    return await request
      .getManyPaginated({
        path: `${path}/dynamically?${queryParams}&idSalesRegion=${idSalesRegion}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const listDynamicallyByCustomerGroup = async (
    customerGroupId: string,
    queryParams: string,
  ): Promise<IPaginationReturn<ICustomer[]>> => {
    const userSession = localStorage.getItem('@PEDIDO-WEB:user');

    const user: IUser = userSession ? JSON.parse(userSession) : null;

    const idSalesRegion = user.seller?.salesRegion?.id ? user.seller?.salesRegion?.id : null;

    return await request
      .getManyPaginated({
        path: `${path}/by-customer-group/dynamically/${customerGroupId}?${queryParams}&idSalesRegion=${idSalesRegion}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const listById = async (id: string): Promise<ICustomer> => {
    return await request
      .getOne({ path: `${path}/${id}`, sendAuthorization: true })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const create = async (customer: ICustomer): Promise<ICustomer> => {
    return await request
      .post({ path, sendAuthorization: true, body: customer })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const update = async (id: string, customer: ICustomer): Promise<ICustomer> => {
    return await request
      .put({ path: `${path}/${id}`, sendAuthorization: true, body: customer })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  const CNPJaQuery = async (cnpj: string): Promise<ICNPJaData> => {
    return await requestCNPJa
      .getOne({ path: `${path}/CNPJQuery/${cnpj}`, sendAuthorization: true })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message, error.status);
      });
  };

  return { listAll, listDynamically, listDynamicallyByCustomerGroup, listById, create, update, CNPJaQuery };
};

export { useCustomerService };
